import "./ScrollSlide.scss";
import { mount } from "@shared/Lib/registry";
import type { Component } from "@shared/Lib/types";
import type {
  AnimationType,
  BackgroundAnimationType,
} from "../../../Lib/Animations/types";
import { getAnimation } from "../../../Lib/Animations/base";
import { selectors } from "../ScrollyTelling/ScrollyTelling";
import { FadeVerticalScrollTrigger } from "../../../Lib/Animations/FadeVertical/animation";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

let idleDetectionActive = false;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let indicatorShown = false;
let hasShownOnce = false;

const SCROLL_NAV_TRIGGER_OFFSET_FACTOR = -0.18;

function initIdleDetection(timeoutMs: number): void {
  if (idleDetectionActive) return;
  idleDetectionActive = true;

  const show = () => {
    if (indicatorShown) return;
    indicatorShown = true;
    hasShownOnce = true;
    window.dispatchEvent(new CustomEvent("scrollIndicator:show"));
  };

  const hide = () => {
    if (!indicatorShown) return;
    indicatorShown = false;
    window.dispatchEvent(new CustomEvent("scrollIndicator:hide"));
  };

  const resetTimer = () => {
    hide();
    if (hasShownOnce) return;
    if (idleTimer !== null) clearTimeout(idleTimer);
    idleTimer = setTimeout(show, timeoutMs);
  };

  window.addEventListener("scroll", resetTimer, { passive: true });
  window.addEventListener("wheel", resetTimer, { passive: true });

  resetTimer();
}

const dispatch = (name: string, detail?: object) => {
  window.dispatchEvent(new CustomEvent(name, detail ? { detail } : undefined));
};

export class ScrollSlide implements Component {
  readonly el: HTMLElement;
  readonly contents: HTMLElement | null;
  readonly timeline: gsap.core.Timeline;
  readonly step: number;
  readonly scrolly: HTMLElement;
  readonly animationType: AnimationType;
  readonly sendTextToHeader: boolean;
  readonly bgControl: BackgroundAnimationType;
  readonly scrollNavTrigger: boolean;
  readonly scrollTopic: string;

  private navST: ScrollTrigger | null = null;

  /**
   * One matchMedia context per slide instance.
   * GSAP automatically reverts all registered tweens / ScrollTriggers
   * when the matched query stops matching (e.g. the user enables
   * "Reduce motion" in their OS settings mid-session).
   */
  private readonly mm = gsap.matchMedia();

  constructor(
    el: HTMLElement,
    scrolly: HTMLElement,
    timeline: gsap.core.Timeline
  ) {
    this.el = el;
    this.contents = el.querySelector(selectors.content as string);
    this.timeline = timeline;
    this.step = Number(this.el.dataset.step);
    this.scrolly = scrolly;
    this.animationType =
      (this.contents?.dataset.animationType as AnimationType) ?? "none";
    this.sendTextToHeader = this.contents?.dataset.sendTextToHeader === "1";
    this.bgControl =
      (this.contents?.dataset.bgControl as BackgroundAnimationType) ?? "none";
    this.scrollNavTrigger = this.contents?.dataset.scrollNavTrigger === "1";
    this.scrollTopic = this.contents?.dataset.scrollTopic ?? "";
    mount(el);

    const timeoutSeconds = Number(
      document.querySelector<HTMLElement>("main")?.dataset
        .scrollIndicatorTimeout ?? 0
    );
    if (timeoutSeconds > 0) {
      initIdleDetection(timeoutSeconds * 1000);
    }

    this.init();
  }

  private get isEmpty(): boolean {
    if (!this.contents) return true;
    return this.contents.children.length === 0;
  }

  private getViewportHeight(): number {
    return window.innerHeight;
  }

  private getStepOffset(offsetFactor = 0): number {
    return (
      this.step * this.getViewportHeight() +
      this.getViewportHeight() * offsetFactor
    );
  }

  private getStepRange(offsetFactor = 0): { start: string; end: string } {
    return {
      start: `top+=${Math.round(this.getStepOffset(offsetFactor))} top`,
      end: `+=${this.getViewportHeight()}`,
    };
  }

  private init(): void {
    if (this.isEmpty) {
      if (this.scrollNavTrigger || this.scrollTopic) {
        this.initNavOnlyTrigger();
      }
      return;
    }

    this.mm.add("(prefers-reduced-motion: no-preference)", () => {
      const animation = getAnimation(this.animationType);

      if (animation === FadeVerticalScrollTrigger) {
        animation({
          timeline: this.timeline,
          section: this.el,
          step: this.step,
          triggerElement: this.scrolly,
          bgControl: this.bgControl,
          sendTextToHeader: this.sendTextToHeader,
        });
      } else {
        animation({
          timeline: this.timeline,
          section: this.el,
          step: this.step,
          triggerElement: this.scrolly,
        });
      }

      if (this.scrollNavTrigger || this.scrollTopic) {
        this.initScrollNav();
      }

      return () => {
        this.navST?.kill();
        this.navST = null;
      };
    });

    this.mm.add("(prefers-reduced-motion: reduce)", () => {
      const targets = [this.el, this.contents].filter(Boolean) as HTMLElement[];
      gsap.set(targets, { clearProps: "opacity,transform,top,left,position" });
    });
  }

  /**
   * Nav-only trigger for empty slides.
   * No pin, no scroll distance — fires when the 1px scrollytelling
   * container crosses the top of the viewport.
   */
  private initNavOnlyTrigger(): void {
    const range = this.getStepRange(SCROLL_NAV_TRIGGER_OFFSET_FACTOR);

    this.navST = ScrollTrigger.create({
      trigger: this.scrolly,
      start: () => range.start,
      end: () => range.end,

      onEnter: () => {
        if (this.scrollNavTrigger) dispatch("scrollNavigation:show");
        if (this.scrollTopic)
          dispatch("scrollNavigation:setTopic", {
            topic: this.scrollTopic,
            direction: "forward",
          });
      },

      onLeaveBack: () => {
        if (this.scrollTopic)
          dispatch("scrollNavigation:setTopic", {
            topic: this.scrollTopic,
            direction: "backward",
          });
        if (this.scrollNavTrigger) dispatch("scrollNavigation:hide");
      },
    });
  }

  private initScrollNav(): void {
    const range = this.getStepRange(SCROLL_NAV_TRIGGER_OFFSET_FACTOR);

    this.navST = ScrollTrigger.create({
      trigger: this.scrolly,
      start: () => range.start,
      end: () => range.end,

      onEnter: () => {
        if (this.scrollNavTrigger) dispatch("scrollNavigation:show");
        if (this.scrollTopic)
          dispatch("scrollNavigation:setTopic", {
            topic: this.scrollTopic,
            direction: "forward",
          });
      },

      onEnterBack: () => {},

      onLeaveBack: () => {
        if (this.scrollTopic)
          dispatch("scrollNavigation:setTopic", {
            topic: this.scrollTopic,
            direction: "backward",
          });
        if (this.scrollNavTrigger) dispatch("scrollNavigation:hide");
      },
    });
  }

  destroy(): void {
    this.mm.revert();
    this.navST?.kill();
    this.navST = null;
  }
}
